import type { ReactNode } from "react";
import { cx } from "@frontend/components/ui";
import { Reveal } from "@frontend/components/site/reveal";
import { Section } from "@frontend/components/site/section";
import {
  ChartIcon,
  ClockIcon,
  DocumentIcon,
  PhoneIcon,
  ReceiptIcon,
} from "@frontend/components/site/icons";
import {
  CallStill,
  DashboardStill,
  LedgerStill,
  ThreadStill,
  TimerStill,
} from "@frontend/components/site/stills";

type Feature = {
  eyebrow: string;
  title: string;
  body: string;
  tags: string[];
  icon: ReactNode;
  still: ReactNode;
  wide?: boolean;
};

const FEATURES: Feature[] = [
  {
    eyebrow: "The record",
    title: "Terms that cannot drift",
    body: "An offer is never edited. Every counter writes a new version with a diff against the last, and accepting freezes the winning terms into a contract both sides can open.",
    tags: ["Versioned offers", "Diff on every counter", "Contract on accept"],
    icon: <DocumentIcon />,
    still: <ThreadStill />,
    wide: true,
  },
  {
    eyebrow: "The clock",
    title: "Timers chase so you don’t",
    body: "Five clocks cover a collab from first offer to final tranche, and both sides watch the same countdown.",
    tags: ["Offer expiry", "Auto-approval"],
    icon: <ClockIcon />,
    still: <TimerStill />,
  },
  {
    eyebrow: "Payments",
    title: "Money you can account for",
    body: "No gateway, no escrow. They pay your UPI or bank and log it; nothing counts as earned until you confirm it landed.",
    tags: ["Paid by stage", "Confirmed only"],
    icon: <ReceiptIcon />,
    still: <LedgerStill />,
  },
  {
    eyebrow: "The call",
    title: "Talk without sharing numbers",
    body: "The platform dials both legs and bridges them, so neither side ever sees a phone number. The recording lands back in the thread.",
    tags: ["15 min per collab", "Recorded"],
    icon: <PhoneIcon />,
    still: <CallStill />,
  },
  {
    eyebrow: "Overview",
    title: "The whole book, one screen",
    body: "What each collab is owed, what has been confirmed, and what you earned this quarter — plus the other side’s record before you say yes.",
    tags: ["Live status", "Payer track record"],
    icon: <ChartIcon />,
    still: <DashboardStill />,
  },
];

function Tags({ tags }: { tags: string[] }) {
  return (
    <ul className="mt-5 flex flex-wrap gap-2">
      {tags.map((tag) => (
        <li
          key={tag}
          className="rounded-full border border-ink/10 bg-white px-2.5 py-1 text-[11px] font-medium tracking-wide text-ink/55"
        >
          {tag}
        </li>
      ))}
    </ul>
  );
}

export function Features() {
  return (
    <Section id="features" className="bg-[#f3f7f4]">
      <Reveal>
        <p className="mkt-kicker">What you get</p>
        <h2 className="mkt-display mt-4 max-w-3xl text-4xl leading-[1.05] text-balance sm:text-5xl lg:text-[3.5rem]">
          Everything a DM thread <span className="mkt-serif text-[#0a9f56]">cannot do</span>
        </h2>
        <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink/55">
          A chat window has no memory, no state and no clock. This has all three.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-5 lg:grid-cols-2">
        {FEATURES.map((feature, index) => (
          <Reveal
            key={feature.title}
            delay={index * 60}
            className={cx("h-full", feature.wide && "lg:col-span-2")}
          >
            <article
              className={cx(
                "mkt-bento group grid h-full gap-7 p-6 sm:p-8",
                feature.wide
                  ? "lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] lg:items-center lg:gap-12"
                  : "grid-rows-[auto_1fr]",
              )}
            >
              <div className="min-w-0">
                <span className="mkt-chip inline-flex size-11 items-center justify-center">
                  {feature.icon}
                </span>
                <p className="mkt-kicker mt-5">{feature.eyebrow}</p>
                <h3 className="mkt-display mt-2.5 text-2xl sm:text-[1.75rem]">{feature.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-ink/55">{feature.body}</p>
                <Tags tags={feature.tags} />
              </div>

              <div className={cx("mkt-tray", !feature.wide && "self-end")}>
                <div className={cx("mkt-tray-clip", feature.wide && "mkt-tray-clip-tall")}>
                  <div className="mkt-tray-still">{feature.still}</div>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
